import type { CollectionResponse } from '@guildrise/shared';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { ApiError, getAuthRevision, subscribeAuthSession } from '../../shared/api.js';
import { loadCollection, readCollection } from './collection-cache.js';

export type CollectionState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly collection: CollectionResponse };

export function useCollection(guildId: string): {
  readonly state: CollectionState;
  readonly retry: () => void;
} {
  const revision = useSyncExternalStore(subscribeAuthSession, getAuthRevision, getAuthRevision);
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState<{
    guildId: string;
    attempt: number;
    revision: number;
    state: CollectionState;
  }>(() => {
    const collection = readCollection(guildId);
    return {
      guildId,
      attempt: 0,
      revision,
      state: collection ? { status: 'ready', collection } : { status: 'loading' },
    };
  });

  useEffect(() => {
    const controller = new AbortController();
    const cached = readCollection(guildId);
    setLoaded({
      guildId,
      attempt,
      revision,
      state: cached ? { status: 'ready', collection: cached } : { status: 'loading' },
    });
    void loadCollection(guildId, controller.signal)
      .then((collection) => {
        if (!controller.signal.aborted)
          setLoaded({ guildId, attempt, revision, state: { status: 'ready', collection } });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setLoaded({
          guildId,
          attempt,
          revision,
          state: {
            status: 'error',
            message: error instanceof ApiError ? error.message : 'Не удалось загрузить коллекцию',
          },
        });
      });
    return () => controller.abort();
  }, [guildId, attempt, revision]);

  return {
    // При смене аккаунта старые данные скрыты уже в первом кадре, до эффекта.
    state:
      loaded.guildId === guildId && loaded.attempt === attempt && loaded.revision === revision
        ? loaded.state
        : { status: 'loading' },
    retry: () => setAttempt((value) => value + 1),
  };
}
